import { createContext, useContext, useEffect, useState } from 'react'
import { onAuthStateChanged, type User as FirebaseUser } from 'firebase/auth'
import { auth } from '@/lib/firebase'
import { getIdToken, readPendingProvision, clearPendingProvision } from '@/lib/auth'

interface Company {
  id: string
  name: string
}

interface AppUser {
  id: string
  authUserId: string
  companyId: string
  role: 'ADMIN' | 'MEMBER' | 'VIEWER'
  fullName: string
  company: Company
}

interface AuthContextType {
  firebaseUser: FirebaseUser | null
  loading: boolean
  appUser: AppUser | null
  company: Company | null
  emailVerified: boolean
  refreshAppUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [appUser, setAppUser] = useState<AppUser | null>(null)
  const [company, setCompany] = useState<Company | null>(null)

  const fetchAppUser = async (user: FirebaseUser | null) => {
    if (!user) {
      setAppUser(null)
      setCompany(null)
      return
    }

    // Don't call backend if email not verified - it will just reject us
    if (!user.emailVerified) {
      setAppUser(null)
      setCompany(null)
      return
    }

    try {
      const token = await getIdToken()
      if (!token) return

      let response = await fetch(`${import.meta.env.VITE_API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.status === 401) {
        // User is authenticated with Firebase but not yet provisioned in our DB.
        // If we have pending provision data from registration, try to provision now.
        const pending = readPendingProvision()
        if (pending) {
          const provisionRes = await fetch(`${import.meta.env.VITE_API_URL}/provision`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(pending),
          })
          if (provisionRes.ok) {
            clearPendingProvision()
            response = await fetch(`${import.meta.env.VITE_API_URL}/auth/me`, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            })
          }
        }
      }

      if (response.ok) {
        const userData = await response.json()
        setAppUser(userData)
        setCompany(userData.company)
      } else {
        console.error('Failed to fetch app user data', response.status)
        setAppUser(null)
        setCompany(null)
      }
    } catch (error) {
      console.error('Error fetching app user:', error)
      setAppUser(null)
      setCompany(null)
    }
  }

  const refreshAppUser = async () => {
    await fetchAppUser(auth.currentUser)
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setFirebaseUser(user)
      await fetchAppUser(user)
      setLoading(false)
    })
    return unsubscribe
  }, [])

  return (
    <AuthContext.Provider
      value={{
        firebaseUser,
        loading,
        appUser,
        company,
        emailVerified: firebaseUser?.emailVerified ?? false,
        refreshAppUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
